/**
 * ResellerStandingPanel (TMMPDM-0022): Standing tab — state, probation window, and months table
 * (each row's own currency; never summed).
 * TMMPDM-0023: renders the detail page's one standing read (no second fetch); keeps its own
 * error + Retry.
 * TMMPDM-0026 (tmm-api 0090): a present `reactivatedAt` adds the reactivated-at line.
 * TMMPDM-0030 (tmm-api 0095): a non-null `saasAuthStatus` adds the "saas-auth status" line with its
 * freshness caption (it can lag by the mirror interval, ~10 minutes).
 * TMMPDM-0037 (tmm-api 0099/0100): the outstanding shortfall and the Repaid column are gone. The
 * header shows the official launch (or why nothing is evaluated without one, with a link to the
 * Agreement tab), the probation deadline and the next notice — each rendered from its own field,
 * shown when the server sets it. Each month shows its shortfall charge's status, linked to that
 * charge on the Billing tab. The clear action sits beside the chip: Keep this reseller… on
 * DEACTIVATION_DUE, End probation… on PROBATION (`ClearStandingDialog`).
 * TMMPDM-0041 (tmm-api 0102): a clear refused with 409 `RESELLER_SUSPENDED_USE_REACTIVATE` hands
 * over to Reactivate… (`ReactivateResellerDialog`), here as on the Overview.
 */
import { Fragment, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { ErrorState, SectionCard, useNotify } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import type { ResellerStandingView } from '@/api/tmm/types';
import { Amount } from '@/components/ui';
import { formatDate, formatMonthYear } from '@/format';
import { StandingChip } from './StandingChip';
import { ResellerChargeChip } from './ResellerChargeChip';
import { ClearStandingDialog, type ClearStandingMode } from './ClearStandingDialog';
import { ReactivateResellerDialog } from './ReactivateResellerDialog';
import { chargeHref } from './settlementFormat';

interface ResellerStandingPanelProps {
  /** The page's one standing read (`useResellerStanding`); `null` while loading or failed. */
  standing: ResellerStandingView | null;
  failure: unknown;
  onRetry: () => void;
  reseller: { id: number; name: string };
  /** Re-read the reseller and its standing (after a clear, or when it moved under us). */
  onChanged: () => void;
}

export function ResellerStandingPanel({
  standing,
  failure,
  onRetry,
  reseller,
  onChanged,
}: ResellerStandingPanelProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [clearMode, setClearMode] = useState<ClearStandingMode | null>(null);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const openClear = (mode: ClearStandingMode) => {
    setProblem(null);
    setClearMode(mode);
  };

  const cleared = (mode: ClearStandingMode) => {
    notify.success(
      mode === 'keep'
        ? t(
            'resellers.overview.keep.done',
            'Kept. The deactivation flag is cleared; its shortfall charges stay open.',
          )
        : t(
            'resellers.standing.endProbation.done',
            'Probation ended. The reseller is in good standing.',
          ),
    );
    onChanged();
  };

  const conflicted = (mode: ClearStandingMode) => {
    setProblem(
      mode === 'keep'
        ? t(
            'resellers.overview.keep.conflict',
            'This reseller is no longer due for deactivation, so there was nothing to clear. The screen has been reloaded.',
          )
        : t(
            'resellers.standing.endProbation.conflict',
            'This reseller is no longer on probation, so there was nothing to end. The screen has been reloaded.',
          ),
    );
    onChanged();
  };

  const outcomeUnknown = () => {
    setProblem(
      t(
        'common.outcomeUnknown',
        "We couldn't confirm the change. The screen has been reloaded — check before trying again.",
      ),
    );
    onChanged();
  };

  // TMMPDM-0041: the dialogs outlive a re-read (the standing is `null` while it reloads): every
  // branch renders them keyed inside the same Box, so a clear refused for a suspended reseller keeps
  // its message while the standing reloads, and hands over to Reactivate….
  const dialogs = (
    <Fragment key="dialogs">
      <ClearStandingDialog
        open={clearMode !== null}
        mode={clearMode ?? 'keep'}
        reseller={reseller}
        onClose={() => setClearMode(null)}
        onCleared={() => cleared(clearMode ?? 'keep')}
        onConflict={() => conflicted(clearMode ?? 'keep')}
        onOutcomeUnknown={outcomeUnknown}
        onSuspended={onChanged}
        standingPending={standing === null && !failure}
        onUseReactivate={() => {
          setProblem(null);
          setReactivateOpen(true);
        }}
        reactivateOffered={standing?.reactivateOffered ?? false}
      />
      <ReactivateResellerDialog
        open={reactivateOpen}
        reseller={reseller}
        standingState={standing?.state ?? null}
        onClose={() => setReactivateOpen(false)}
        onReactivated={() => {
          notify.success(
            t('resellers.reactivate.done', '{{name}} is active again.', { name: reseller.name }),
          );
          onChanged();
        }}
        onConflict={() => {
          setProblem(
            t(
              'resellers.reactivate.conflict',
              "Reactivate was refused because this reseller's status changed. The screen has been reloaded.",
            ),
          );
          onChanged();
        }}
        onNotDeactivated={() => {
          setProblem(
            t(
              'resellers.reactivate.notDeactivated',
              "This partner isn't deactivated. Use Reactivate on the status toggle to lift an ordinary suspension.",
            ),
          );
          onChanged();
        }}
        onOutcomeUnknown={outcomeUnknown}
      />
    </Fragment>
  );

  if (failure) {
    return (
      <Box>
        <ErrorState
          error={failure}
          message={t('resellers.standing.loadError', 'Could not load standing.')}
          onRetry={onRetry}
        />
        {dialogs}
      </Box>
    );
  }

  if (!standing) {
    return (
      <Box>
        <LoadingSpinner />
        {dialogs}
      </Box>
    );
  }

  const probationWindow =
    standing.probationStartedOn || standing.probationEndsOn
      ? `${formatDate(standing.probationStartedOn, locale)}–${formatDate(standing.probationEndsOn, locale)}`
      : '—';

  const clearAction: ClearStandingMode | null =
    standing.state === 'DEACTIVATION_DUE'
      ? 'keep'
      : standing.state === 'PROBATION'
        ? 'endProbation'
        : null;

  return (
    <Box>
      {problem ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setProblem(null)}>
          {problem}
        </Alert>
      ) : null}

      {standing.platformManagedSince ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'partners.platformManaged.alert',
            'Platform-managed since {{date}}. Settlements are retained; reactivate to resume payouts.',
            { date: formatDate(standing.platformManagedSince, locale) },
          )}
        </Alert>
      ) : null}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { sm: 'center' }, mb: 2, flexWrap: 'wrap' }}
        useFlexGap
      >
        <StandingChip state={standing.state} />
        <Typography variant="body2" color="text.secondary">
          {t('resellers.standing.probationWindow', 'Probation window')}: {probationWindow}
        </Typography>
        {clearAction === 'keep' ? (
          <Button size="small" variant="outlined" onClick={() => openClear('keep')}>
            {t('resellers.overview.keep.button', 'Keep this reseller…')}
          </Button>
        ) : null}
        {clearAction === 'endProbation' ? (
          <Button size="small" variant="outlined" onClick={() => openClear('endProbation')}>
            {t('resellers.standing.endProbation.button', 'End probation…')}
          </Button>
        ) : null}
        {standing.reactivateOffered ? (
          <Button size="small" variant="outlined" onClick={() => setReactivateOpen(true)}>
            {t('resellers.reactivate.button', 'Reactivate…')}
          </Button>
        ) : null}
      </Stack>

      <SectionCard>
        <Stack spacing={0.5}>
          <Typography variant="body2">
            <Typography component="span" variant="body2" color="text.secondary">
              {t('resellers.standing.officialLaunch', 'Official launch')}:{' '}
            </Typography>
            {standing.officialLaunchOn !== null ? (
              formatDate(standing.officialLaunchOn, locale)
            ) : (
              <>
                {/* TMMPDM-0044: with months already evaluated, "no month is evaluated" is untrue. */}
                {standing.months.length > 0
                  ? t('resellers.standing.launchNoneSet', 'No official launch is set.')
                  : t(
                      'resellers.standing.launchNotSet',
                      'Not set — no month is evaluated until the agreement sets one.',
                    )}{' '}
                <Link component={RouterLink} to="?tab=agreement" underline="always">
                  {t('resellers.standing.openAgreement', 'Open the Agreement tab')}
                </Link>
              </>
            )}
          </Typography>
          {standing.probationDeadline !== null ? (
            <Typography variant="body2">
              <Typography component="span" variant="body2" color="text.secondary">
                {t('resellers.standing.probationDeadline', 'Probation deadline')}:{' '}
              </Typography>
              {t('resellers.standing.judgedOn', 'Judged on {{date}}', {
                date: formatDate(standing.probationDeadline, locale),
              })}
            </Typography>
          ) : null}
          <Typography variant="body2">
            <Typography component="span" variant="body2" color="text.secondary">
              {t('resellers.standing.nextNotice', 'Next notice')}:{' '}
            </Typography>
            {standing.nextNoticeOn !== null
              ? formatDate(standing.nextNoticeOn, locale)
              : t('resellers.standing.noNotice', 'None scheduled')}
          </Typography>
        </Stack>

        {standing.saasAuthStatus ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t('resellers.standing.saasAuthStatus', 'saas-auth status')}:{' '}
              {standing.saasAuthStatus === 'ACTIVE'
                ? t('resellers.standing.saasAuthActive', 'Active')
                : t('resellers.standing.saasAuthSuspended', 'Suspended')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('resellers.standing.saasAuthCaption', 'As tmm-api last saw it.')}
            </Typography>
          </Box>
        ) : null}

        {standing.reactivatedAt ? (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t(
              'resellers.standing.reactivated',
              'Reactivated {{date}}. The minimum commitment restarts from then.',
              { date: formatDate(standing.reactivatedAt, locale) },
            )}
          </Typography>
        ) : null}
      </SectionCard>

      {standing.months.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {standing.officialLaunchOn !== null
            ? t(
                'resellers.standing.notEvaluated',
                'Not evaluated yet. Each month from the official launch is evaluated on the 2nd of the next month.',
              )
            : t(
                'resellers.standing.notEvaluatedNoLaunch',
                'Not evaluated: there is no official launch yet.',
              )}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('resellers.standing.tableAria', 'Standing by month')}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('resellers.standing.col.month', 'Month')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  {t('resellers.standing.col.minimum', 'Minimum')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  {t('resellers.standing.col.earned', 'Earned')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  {t('resellers.standing.col.shortfall', 'Shortfall')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('resellers.standing.col.shortfallCharge', 'Shortfall charge')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {standing.months.map((row) => (
                <TableRow key={`${row.month}-${row.currency}`}>
                  <TableCell>{formatDate(row.month, locale)}</TableCell>
                  <TableCell align="right">
                    <Amount minor={row.minimumMinor} currency={row.currency} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount minor={row.earnedMinor} currency={row.currency} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount minor={row.shortfallMinor} currency={row.currency} locale={locale} />
                  </TableCell>
                  <TableCell>
                    {row.chargeStatus !== null ? (
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <ResellerChargeChip status={row.chargeStatus} />
                        {row.shortfallChargeId !== null ? (
                          <Link
                            component={RouterLink}
                            to={chargeHref(row.shortfallChargeId)}
                            underline="always"
                            variant="body2"
                            aria-label={t(
                              'resellers.standing.viewChargeAria',
                              'View charge for {{month}}',
                              { month: formatMonthYear(row.month, locale) },
                            )}
                          >
                            {t('resellers.standing.viewCharge', 'View charge')}
                          </Link>
                        ) : null}
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {dialogs}
    </Box>
  );
}
